import { Fragment, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { FiltruData } from '../../components/ui/FiltruData'
import { NoteEaAmazon } from './NoteEaAmazon'

interface Sumar {
  incasatClienti: number
  taxeComenzi: number
  publicitate: number
  alteTaxe: number
  platiCard: number
  compensariPiete: number
  despagubiri: number
  altele: number
  total: number
}

interface DecontareRow extends Sumar {
  numar: string
  primaData: string
  ultimaData: string
  randuri: number
  deVerificat: number
}

interface IncasariResponse {
  items: DecontareRow[]
  sold: number
  facturiNeincasate: number
  luni: string[]
}

interface Tranzactie {
  id: number
  data: string
  tip: string
  categorie: string
  comanda: string | null
  descriere: string | null
  brut: number
  taxeAmazon: number
  total: number
  potrivire: string | null
  document: { numar: string | null; fel: string; brut: number } | null
  /** Factura de publicitate reținută (rândurile Werbekosten). */
  factura: string | null
  /** Plata din extrasul Wise (rândurile „plată de pe card”). */
  wise: { data: string; suma: number } | null
}

interface DetaliuResponse {
  numar: string
  tranzactii: Tranzactie[]
}

interface ImportResult {
  randuri: number
  noi: number
  existente: number
  decontari: number
  comenzi: Record<string, number>
}

const fmt = (n: number) =>
  n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const zi = (s: string) => `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}`

const CATEGORIE: Record<string, string> = {
  vanzare: 'Vânzare',
  rambursare: 'Rambursare',
  publicitate: 'Publicitate',
  taxa: 'Taxă Amazon',
  plata_card: 'Plată de pe card',
  compensare_piete: 'Compensare între piețe',
  despagubire: 'Despăgubire stoc',
  altele: 'Altele',
}

const POTRIVIRE: Record<string, { label: string; cls: string }> = {
  potrivita: { label: 'Potrivită', cls: 'bg-green-100 text-green-700' },
  vine: { label: 'Vine, fără factură', cls: 'bg-slate-100 text-slate-600' },
  suma_diferita: { label: 'Sumă diferită', cls: 'bg-amber-100 text-amber-800' },
  fara_factura: { label: 'Fără factură', cls: 'bg-red-100 text-red-700' },
  mai_multe_facturi: { label: 'Mai multe facturi', cls: 'bg-amber-100 text-amber-800' },
}

function eroare(e: unknown, implicit: string): string {
  return (
    (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? implicit
  )
}

/**
 * Amazon → Încasări: decontările din raportul de tranzacții (Seller Central → Finance → Payment
 * reports), cu încasările legate de facturile de vânzare. Notele pentru EA vin într-o etapă
 * următoare. Design: docs/superpowers/specs/2026-10-31-amazon-incasari-design.md
 */
export function IncasariAmazonPage() {
  const qc = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [de, setDe] = useState('')
  const [pana, setPana] = useState('')
  const [deschisa, setDeschisa] = useState<string | null>(null)
  const [fila, setFila] = useState<'decontari' | 'wise' | 'note'>('decontari')

  const { data, isLoading } = useQuery<IncasariResponse>({
    queryKey: ['amazon-incasari', de, pana],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (de) params.set('from', de)
      if (pana) params.set('to', pana)
      return (await api.get(`/amazon/incasari?${params}`)).data
    },
  })

  const detaliu = useQuery<DetaliuResponse>({
    queryKey: ['amazon-incasari', 'decontare', deschisa],
    enabled: deschisa != null,
    queryFn: async () => (await api.get(`/amazon/incasari/${deschisa}`)).data,
  })

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData()
      fd.append('file', file)
      return (await api.post<ImportResult>('/amazon/incasari/raport', fd)).data
    },
    onSuccess: (r) => {
      toast.success(
        `Raport importat: ${r.randuri} rânduri (${r.noi} noi, ${r.existente} deja importate), ${r.decontari} decontări. Comenzi potrivite: ${r.comenzi.potrivita ?? 0}.`,
      )
      qc.invalidateQueries({ queryKey: ['amazon-incasari'] })
      qc.invalidateQueries({ queryKey: ['amazon-vanzari'] })
      if (fileRef.current) fileRef.current.value = ''
    },
    onError: (e) => toast.error(eroare(e, 'Eroare la import')),
  })

  const items = data?.items ?? []

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Amazon — Încasări</h1>
          <p className="text-sm text-slate-500">
            Decontările Amazon: ce s-a încasat din vânzări, ce a reținut Amazon și ce s-a tras de pe
            card. Încarcă raportul de tranzacții din Seller Central (Finance → Payment reports →
            Reports Repository).
          </p>
        </div>
        <div>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) upload.mutate(f)
            }}
          />
          <button
            className="whitespace-nowrap rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
            disabled={upload.isPending}
            onClick={() => fileRef.current?.click()}
          >
            {upload.isPending ? 'Se încarcă…' : 'Încarcă raport tranzacții'}
          </button>
        </div>
      </div>

      <div className="flex gap-1 border-b text-sm">
        {(
          [
            ['decontari', 'Decontări'],
            ['wise', 'Card Wise'],
            ['note', 'Note EA'],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            className={`-mb-px border-b-2 px-4 py-2 ${
              fila === k ? 'border-slate-900 font-medium' : 'border-transparent text-slate-500'
            }`}
            onClick={() => setFila(k)}
          >
            {label}
          </button>
        ))}
      </div>

      {fila === 'wise' && <CardWise />}
      {fila === 'note' && <NoteEaAmazon />}

      {fila === 'decontari' && data && (
        <Card className="p-4">
          <div className="flex flex-wrap gap-6 text-sm">
            <div>
              <div className="text-slate-500">Sold Amazon (toate decontările)</div>
              <div className={`text-lg font-semibold ${data.sold < 0 ? 'text-red-600' : ''}`}>
                {fmt(data.sold)} EUR
              </div>
            </div>
            <div>
              <div className="text-slate-500">Decontări</div>
              <div className="text-lg font-semibold">{items.length}</div>
            </div>
            <div>
              <div className="text-slate-500">Facturi de vânzare neîncasate</div>
              <div className="text-lg font-semibold">{data.facturiNeincasate}</div>
            </div>
          </div>
        </Card>
      )}

      {fila === 'decontari' && (
        <>
          <Card className="p-4">
            <FiltruData
              eticheta="Data decontării (ultima tranzacție)"
              de={de}
              pana={pana}
              onChange={(a, b) => (setDe(a), setPana(b))}
            />
          </Card>

          <Card className="overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead className="border-b bg-slate-50 text-left text-slate-500">
                <tr>
                  <th className="px-3 py-2">Decontare</th>
                  <th className="px-3 py-2">Perioada</th>
                  <th className="px-3 py-2 text-right">Încasat din vânzări</th>
                  <th className="px-3 py-2 text-right">Comision + FBA</th>
                  <th className="px-3 py-2 text-right">Publicitate</th>
                  <th className="px-3 py-2 text-right">Alte taxe</th>
                  <th className="px-3 py-2 text-right">Plăți de pe card</th>
                  <th className="px-3 py-2 text-right">Altele</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2">De verificat</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={10} className="px-3 py-6 text-center text-slate-400">
                      Se încarcă…
                    </td>
                  </tr>
                )}
                {!isLoading && items.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-3 py-6 text-center text-slate-400">
                      Nicio decontare. Încarcă raportul de tranzacții Amazon.
                    </td>
                  </tr>
                )}
                {items.map((d) => (
                  <Fragment key={d.numar}>
                    <tr
                      className="cursor-pointer border-b last:border-0 hover:bg-slate-50"
                      onClick={() => setDeschisa(deschisa === d.numar ? null : d.numar)}
                    >
                      <td className="px-3 py-2 font-medium">
                        {deschisa === d.numar ? '▾' : '▸'} {d.numar}
                      </td>
                      <td className="px-3 py-2 text-slate-600">
                        {zi(d.primaData)} – {zi(d.ultimaData)}
                      </td>
                      <td className="px-3 py-2 text-right">{fmt(d.incasatClienti)}</td>
                      <td className="px-3 py-2 text-right">{fmt(d.taxeComenzi)}</td>
                      <td className="px-3 py-2 text-right">{fmt(d.publicitate)}</td>
                      <td className="px-3 py-2 text-right">{fmt(d.alteTaxe)}</td>
                      <td className="px-3 py-2 text-right">{fmt(d.platiCard)}</td>
                      <td
                        className="px-3 py-2 text-right text-slate-500"
                        title="Compensări între piețe, despăgubiri de stoc și altele"
                      >
                        {fmt(d.compensariPiete + d.despagubiri + d.altele)}
                      </td>
                      <td className="px-3 py-2 text-right font-medium">{fmt(d.total)}</td>
                      <td className="px-3 py-2">
                        {d.deVerificat > 0 ? (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                            {d.deVerificat}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                    {deschisa === d.numar && (
                      <tr className="border-b bg-slate-50/50">
                        <td colSpan={10} className="px-3 py-3">
                          {detaliu.isLoading && <div className="text-slate-400">Se încarcă…</div>}
                          {detaliu.data && (
                            <table className="w-full text-xs">
                              <thead className="text-left text-slate-500">
                                <tr>
                                  <th className="px-2 py-1">Data</th>
                                  <th className="px-2 py-1">Ce e</th>
                                  <th className="px-2 py-1">Comanda / descriere</th>
                                  <th className="px-2 py-1 text-right">Brut client</th>
                                  <th className="px-2 py-1 text-right">Taxe pe comandă</th>
                                  <th className="px-2 py-1 text-right">Total</th>
                                  <th className="px-2 py-1">Document</th>
                                </tr>
                              </thead>
                              <tbody>
                                {detaliu.data.tranzactii.map((t) => {
                                  const p = t.potrivire ? POTRIVIRE[t.potrivire] : null
                                  return (
                                    <tr key={t.id} className="border-t border-slate-200">
                                      <td className="px-2 py-1 whitespace-nowrap">{zi(t.data)}</td>
                                      <td className="px-2 py-1">
                                        {CATEGORIE[t.categorie] ?? t.tip}
                                      </td>
                                      <td className="px-2 py-1 text-slate-600">
                                        {t.comanda ?? t.descriere}
                                      </td>
                                      <td className="px-2 py-1 text-right">
                                        {t.brut ? fmt(t.brut) : ''}
                                      </td>
                                      <td className="px-2 py-1 text-right">
                                        {t.taxeAmazon ? fmt(t.taxeAmazon) : ''}
                                      </td>
                                      <td className="px-2 py-1 text-right font-medium">
                                        {fmt(t.total)}
                                      </td>
                                      <td className="px-2 py-1">
                                        {t.document?.numar && (
                                          <span className="mr-1">{t.document.numar}</span>
                                        )}
                                        {t.factura && <span className="mr-1">{t.factura}</span>}
                                        {t.wise && (
                                          <span className="text-green-700">
                                            Wise {zi(t.wise.data)}, {fmt(t.wise.suma)} lei
                                          </span>
                                        )}
                                        {t.categorie === 'plata_card' && !t.wise && (
                                          <span className="text-amber-700">fără plată în Wise</span>
                                        )}
                                        {p && (
                                          <span className={`rounded-full px-2 py-0.5 ${p.cls}`}>
                                            {p.label}
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  )
}

interface WiseRow {
  id: number
  wiseId: string
  data: string
  suma: number
  descriere: string | null
  comerciant: string | null
  categorie: string
  monedaOriginala: string | null
  sumaOriginala: number | null
  abonament: string | null
  plataAmazon: { decontare: string; data: string; randuri: number; total: number } | null
}

interface WiseResponse {
  items: WiseRow[]
  peCategorii: Record<string, { n: number; lei: number }>
}

interface WiseImport {
  randuri: number
  noi: number
  existente: number
  platiLegate: number
  platiNelegate: number
  abonamenteLegate: number
}

const CATEGORII_WISE: { key: string; label: string }[] = [
  { key: '', label: 'Toate' },
  { key: 'plata_amazon', label: 'Plăți trase de Amazon' },
  { key: 'abonament_amazon', label: 'Abonament Amazon UK' },
  { key: 'alimentare', label: 'Alimentări' },
  { key: 'comision_wise', label: 'Comisioane Wise' },
  { key: 'verificare_card', label: 'Verificări de card' },
  { key: 'altele', label: 'Altele' },
]
const ETICHETA_WISE = Object.fromEntries(CATEGORII_WISE.map((c) => [c.key, c.label]))

/**
 * Fila „Card Wise”: extrasul contului Wise (xlsx), pe categorii, cu plata trasă de Amazon legată de
 * rândul ei din decontare și abonamentul UK legat de factura lui. Etapa 3 din design.
 */
function CardWise() {
  const qc = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [categorie, setCategorie] = useState('plata_amazon')

  const { data, isLoading } = useQuery<WiseResponse>({
    queryKey: ['amazon-wise', categorie],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (categorie) params.set('categorie', categorie)
      return (await api.get(`/amazon/wise?${params}`)).data
    },
  })

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData()
      fd.append('file', file)
      return (await api.post<WiseImport>('/amazon/wise/extras', fd)).data
    },
    onSuccess: (r) => {
      toast.success(
        `Extras Wise: ${r.randuri} rânduri (${r.noi} noi, ${r.existente} deja importate). Plăți Amazon legate: ${r.platiLegate}, nelegate: ${r.platiNelegate}. Abonamente legate: ${r.abonamenteLegate}.`,
      )
      qc.invalidateQueries({ queryKey: ['amazon-wise'] })
      qc.invalidateQueries({ queryKey: ['amazon-incasari'] })
      qc.invalidateQueries({ queryKey: ['amazon-fees'] })
      if (fileRef.current) fileRef.current.value = ''
    },
    onError: (e) => toast.error(eroare(e, 'Eroare la import')),
  })

  const items = data?.items ?? []
  const pe = data?.peCategorii ?? {}

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-slate-500">
          Extrasul contului Wise, de pe al cărui card Amazon trage soldul negativ. Din extras nu se
          păstrează numele titularului și cifrele cardului.
        </p>
        <div>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) upload.mutate(f)
            }}
          />
          <button
            className="whitespace-nowrap rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
            disabled={upload.isPending}
            onClick={() => fileRef.current?.click()}
          >
            {upload.isPending ? 'Se încarcă…' : 'Încarcă extras Wise'}
          </button>
        </div>
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap gap-6 text-sm">
          {CATEGORII_WISE.filter((c) => c.key && c.key !== 'altele').map((c) => (
            <div key={c.key}>
              <div className="text-slate-500">{c.label}</div>
              <div className="text-lg font-semibold">{fmt(pe[c.key]?.lei ?? 0)} lei</div>
            </div>
          ))}
        </div>
      </Card>

      <div className="flex flex-wrap gap-2 text-sm">
        {CATEGORII_WISE.map((c) => (
          <button
            key={c.key || 'toate'}
            className={`rounded-full px-3 py-1 ${
              categorie === c.key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'
            }`}
            onClick={() => setCategorie(c.key)}
          >
            {c.label}{' '}
            <span className="opacity-60">
              ({c.key === '' ? Object.values(pe).reduce((s, v) => s + v.n, 0) : (pe[c.key]?.n ?? 0)}
              )
            </span>
          </button>
        ))}
      </div>

      <Card className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="border-b bg-slate-50 text-left text-slate-500">
            <tr>
              <th className="px-3 py-2">Data</th>
              <th className="px-3 py-2">Descriere</th>
              <th className="px-3 py-2">Categorie</th>
              <th className="px-3 py-2 text-right">Lei</th>
              <th className="px-3 py-2 text-right">Suma originală</th>
              <th className="px-3 py-2">Legat de</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-slate-400">
                  Se încarcă…
                </td>
              </tr>
            )}
            {!isLoading && items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-slate-400">
                  Nimic aici. Încarcă extrasul Wise.
                </td>
              </tr>
            )}
            {items.map((w) => (
              <tr key={w.id} className="border-b last:border-0 hover:bg-slate-50">
                <td className="whitespace-nowrap px-3 py-2">{zi(w.data)}</td>
                <td className="px-3 py-2 text-slate-600">{w.comerciant ?? w.descriere}</td>
                <td className="px-3 py-2">{ETICHETA_WISE[w.categorie] ?? w.categorie}</td>
                <td className={`px-3 py-2 text-right ${w.suma < 0 ? '' : 'text-green-700'}`}>
                  {fmt(w.suma)}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right text-slate-500">
                  {w.sumaOriginala != null && w.monedaOriginala
                    ? `${fmt(w.sumaOriginala)} ${w.monedaOriginala}`
                    : ''}
                </td>
                <td className="px-3 py-2 text-xs">
                  {w.plataAmazon ? (
                    <span className="text-green-700">
                      decontarea {w.plataAmazon.decontare}, {zi(w.plataAmazon.data)}
                      {w.plataAmazon.randuri > 1 ? ` (${w.plataAmazon.randuri} rânduri)` : ''}
                    </span>
                  ) : w.abonament ? (
                    <span className="text-green-700">factura {w.abonament}</span>
                  ) : w.categorie === 'plata_amazon' || w.categorie === 'abonament_amazon' ? (
                    <span className="text-amber-700">nelegată încă</span>
                  ) : (
                    ''
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  )
}
